/**
 * Color utility functions for generating lighter shades of colors
 * Used for multi-level pie charts where children use lighter shades of parent colors
 */

/**
 * Convert hex color to HSL object
 * @param hex - Hex color string (e.g., "#7856FF")
 * @returns HSL object with h, s, l values (0-360, 0-100, 0-100)
 */
export const hexToHsl = (hex: string): { h: number; s: number; l: number } => {
  // Remove # if present
  const cleanHex = hex.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

/**
 * Convert HSL to hex color
 * @param h - Hue (0-360)
 * @param s - Saturation (0-100)
 * @param l - Lightness (0-100)
 * @returns Hex color string
 */
export const hslToHex = (h: number, s: number, l: number): string => {
  const hNorm = h / 360;
  const sNorm = s / 100;
  const lNorm = l / 100;

  let r: number, g: number, b: number;

  if (sNorm === 0) {
    r = g = b = lNorm; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = lNorm < 0.5 ? lNorm * (1 + sNorm) : lNorm + sNorm - lNorm * sNorm;
    const p = 2 * lNorm - q;

    r = hue2rgb(p, q, hNorm + 1 / 3);
    g = hue2rgb(p, q, hNorm);
    b = hue2rgb(p, q, hNorm - 1 / 3);
  }

  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Lighten a color by increasing its lightness
 * @param hex - Base hex color
 * @param lightnessIncrease - Percentage to increase lightness (0-100)
 * @param maxLightness - Maximum lightness to prevent colors from becoming too light
 * @returns Lightened hex color
 */
export const lightenColor = (
  hex: string,
  lightnessIncrease: number,
  maxLightness: number = 95
): string => {
  const hsl = hexToHsl(hex);
  const newLightness = Math.min(maxLightness, hsl.l + lightnessIncrease);
  return hslToHex(hsl.h, hsl.s, newLightness);
};

/**
 * Interpolate lightness between two HSL colors
 * @param firstHsl - First HSL color (higher value, darker)
 * @param lastHsl - Last HSL color (lower value, lighter)
 * @param factor - Interpolation factor (0 = first color, 1 = last color)
 * @returns Interpolated HSL color
 */
const interpolateHslLightness = (
  firstHsl: { h: number; s: number; l: number },
  lastHsl: { h: number; s: number; l: number },
  factor: number
): { h: number; s: number; l: number } => {
  // Clamp factor between 0 and 1
  const clampedFactor = Math.max(0, Math.min(1, factor));
  
  // Interpolate lightness
  const lightness = firstHsl.l + (lastHsl.l - firstHsl.l) * clampedFactor;
  
  // Ensure lightness stays within bounds
  const clampedLightness = Math.max(firstHsl.l, Math.min(lastHsl.l, lightness));
  
  // Keep hue and saturation from parent (use first color's hue/saturation)
  return {
    h: firstHsl.h,
    s: firstHsl.s,
    l: clampedLightness,
  };
};

/**
 * Generate an array of colors for children using bounded interpolation
 * Children with same values get the same color
 * Lightness range is distributed across all levels based on total number of levels
 * @param parentColor - Base color for the parent
 * @param children - Array of child data objects with labels.count or labels.percentage
 * @param level - Current level (1-based, where 1 is first children level)
 * @param totalLevels - Total number of levels (including parent level 0)
 * @returns Array of hex colors, one per child, interpolated between first and last
 */
export const generateChildColors = (
  parentColor: string,
  children: Array<{ labels: { count?: number; percentage?: number } }>,
  level: number = 1,
  totalLevels: number = 2
): string[] => {
  if (!children || children.length === 0) return [];
  
  // Maximum lightness to prevent colors from becoming too light/white
  const MAX_LIGHTNESS = 80;
  // Minimum saturation to maintain color visibility (prevent graying out)
  const MIN_SATURATION = 20;
  
  // Distribute lightness range across all child levels
  // Total range: 10% (start) to 75% (end) = 65% range
  // This range is distributed across (totalLevels - 1) child levels
  const MIN_LIGHTNESS_INCREASE = 10; // Start at 10% lighter
  const MAX_LIGHTNESS_INCREASE = 75; // End at 75% lighter (cap to prevent white)
  const TOTAL_RANGE = MAX_LIGHTNESS_INCREASE - MIN_LIGHTNESS_INCREASE; // 65%
  
  // Calculate the range for this specific level
  // Each level gets a portion of the total range
  const numChildLevels = totalLevels - 1; // Exclude parent level 0
  const rangePerLevel = TOTAL_RANGE / numChildLevels;
  
  // Calculate the base lightness for this level (start of this level's range)
  const levelBaseLightness = MIN_LIGHTNESS_INCREASE + (level - 1) * rangePerLevel;
  // Calculate the end of this level's range
  const levelEndLightness = MIN_LIGHTNESS_INCREASE + level * rangePerLevel;
  
  // The range for children within this level
  const withinLevelRange = levelEndLightness - levelBaseLightness;
  
  // Get parent HSL to calculate bounds properly
  const parentHsl = hexToHsl(parentColor);
  
  // Calculate first color lightness for this level: parent + levelBaseLightness, but cap at max
  const firstLightness = Math.min(MAX_LIGHTNESS, parentHsl.l + levelBaseLightness);
  // Maintain minimum saturation to keep colors visible
  const firstSaturation = Math.max(MIN_SATURATION, parentHsl.s);
  const firstHsl = {
    h: parentHsl.h,
    s: firstSaturation,
    l: firstLightness,
  };
  const firstColor = hslToHex(firstHsl.h, firstSaturation, firstLightness);
  
  // Calculate last color lightness for this level: parent + levelEndLightness, but cap at max
  const lastLightness = Math.min(MAX_LIGHTNESS, parentHsl.l + levelEndLightness);
  // Maintain minimum saturation for last color too
  const lastSaturation = Math.max(MIN_SATURATION, parentHsl.s);
  const lastHsl = {
    h: parentHsl.h,
    s: lastSaturation,
    l: lastLightness,
  };
  const lastColor = hslToHex(lastHsl.h, lastSaturation, lastLightness);
  
  // If only one child, use first color (start of this level's range)
  if (children.length === 1) {
    return [firstColor];
  }
  
  // Group children by their count value (same values = same color)
  const valueToChildren = new Map<number, number[]>();
  const childValues: number[] = [];
  
  children.forEach((child, index) => {
    const value = child.labels.count ?? child.labels.percentage ?? 0;
    childValues[index] = value;
    
    if (!valueToChildren.has(value)) {
      valueToChildren.set(value, []);
    }
    valueToChildren.get(value)!.push(index);
  });
  
  // Get unique values and sort in descending order (largest first)
  const uniqueValues = Array.from(valueToChildren.keys()).sort((a, b) => b - a);
  
  // If all children have the same value, all get first color
  if (uniqueValues.length === 1) {
    return new Array(children.length).fill(firstColor);
  }
  
  // Calculate min and max values for interpolation
  const maxValue = uniqueValues[0];
  const minValue = uniqueValues[uniqueValues.length - 1];
  const valueRange = maxValue - minValue;
  
  // Create color map for unique values
  const valueToColor = new Map<number, string>();
  
  uniqueValues.forEach((value) => {
    // Calculate interpolation factor: 0 for max value (first color), 1 for min value (last color)
    const factor = valueRange > 0 ? (maxValue - value) / valueRange : 0;
    
    // Interpolate HSL lightness
    const interpolatedHsl = interpolateHslLightness(firstHsl, lastHsl, factor);
    
    // Ensure final lightness doesn't exceed MAX_LIGHTNESS
    const finalLightness = Math.min(MAX_LIGHTNESS, interpolatedHsl.l);
    // Ensure saturation doesn't drop below minimum to prevent white/gray colors
    const finalSaturation = Math.max(MIN_SATURATION, interpolatedHsl.s);
    
    // Convert to hex
    const color = hslToHex(interpolatedHsl.h, finalSaturation, finalLightness);
    valueToColor.set(value, color);
  });
  
  // Map colors back to all children (same value = same color)
  return childValues.map((value) => valueToColor.get(value) || firstColor);
};

/**
 * Recursively generate colors for all levels
 * @param baseColors - Colors for level 0 (top level)
 * @param levelData - Data organized by level
 * @param maxLevel - Maximum depth
 * @returns Color map: { [level]: { [index]: color } }
 */
export const generateColorsForAllLevels = (
  baseColors: string[],
  levelData: { [level: number]: any[] },
  maxLevel: number
): { [level: number]: { [index: number]: string } } => {
  const levelColors: { [level: number]: { [index: number]: string } } = {};
  
  // Level 0 uses base colors
  if (levelData[0]) {
    levelColors[0] = {};
    levelData[0].forEach((item, index) => {
      levelColors[0][index] = baseColors[index % baseColors.length];
    });
  }
  
  // Generate colors for each subsequent level
  for (let level = 1; level <= maxLevel; level++) {
    if (!levelData[level]) continue;
    
    levelColors[level] = {};
    
    // Group items by their parent path (full path to identify unique parent)
    const itemsByParentPath = new Map<string, any[]>();
    levelData[level].forEach((item) => {
      const parentPath = item.labels.parentPath;
      if (parentPath.length > 0) {
        // Use full parent path as key (excluding the last element which is this item's index)
        const parentPathKey = parentPath.slice(0, -1).join(',');
        if (!itemsByParentPath.has(parentPathKey)) {
          itemsByParentPath.set(parentPathKey, []);
        }
        itemsByParentPath.get(parentPathKey)!.push(item);
      }
    });
    
    // Generate colors for each parent's children
    itemsByParentPath.forEach((children, parentPathKey) => {
      // Get parent color from previous level
      const parentLevel = level - 1;
      const parentPath = children[0].labels.parentPath.slice(0, -1);
      
      // Find parent color
      let parentColor = baseColors[0]; // fallback
      
      if (parentLevel === 0) {
        // Level 1 children: parent is at level 0
        const parentIndex = parentPath[0];
        if (levelColors[0] && levelColors[0][parentIndex] !== undefined) {
          parentColor = levelColors[0][parentIndex];
        }
      } else if (levelColors[parentLevel] && levelData[parentLevel]) {
        // For deeper levels, find parent by matching full parentPath
        const parentItem = levelData[parentLevel].find((item) => {
          const itemPath = item.labels.parentPath;
          return itemPath.length === parentPath.length &&
            itemPath.every((val: number, idx: number) => val === parentPath[idx]);
        });
        if (parentItem) {
          const parentItemIndex = parentItem.labels.index;
          parentColor = levelColors[parentLevel][parentItemIndex] || parentColor;
        }
      }
      
      // Generate colors for children
      // Pass totalLevels (maxLevel + 1) to distribute range across all levels
      const totalLevels = maxLevel + 1;
      const childColors = generateChildColors(parentColor, children, level, totalLevels);
      
      // Assign colors to children
      children.forEach((child, childIdx) => {
        const childIndex = child.labels.index;
        levelColors[level][childIndex] = childColors[childIdx];
      });
    });
  }
  
  return levelColors;
};

